import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, ChevronDown, Globe, Search, Check, Map, HelpCircle } from 'lucide-react';
import { RiMenu2Fill } from 'react-icons/ri';
import { GrRestroomWomen } from 'react-icons/gr';
import { TbDisabled } from 'react-icons/tb';
import { 
  MdOutlineVolunteerActivism,
  MdInfo,
  MdBusiness,
  MdGroups,
  MdHandshake,
  MdComputer,
  MdDescription,
  MdReportProblem,
  MdQuiz,
  MdSecurity,
  MdArticle,
  MdGavel,
  MdEvent,
  MdStar,
  MdEmojiEvents,
  MdPublic,
  MdMedicalServices,
  MdVerified
} from 'react-icons/md';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { setLanguage } from '@/features/i18n/i18nSlice';
import { cn } from '@/lib/utils';

export function Header() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  // kept for potential future use with per-item dropdowns
  // const [desktopOpenSection, setDesktopOpenSection] = useState<string | null>(null);
  const [megaOpen, setMegaOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Toggle between country and ministry names (manual)
  const [showIraq, setShowIraq] = useState(true);
  const currentLang = useAppSelector((state) => state.i18n.language);
  const langRef = useRef<HTMLDivElement | null>(null);
  const dir = useAppSelector((s) => s.i18n.dir);
  const isRTL = dir === 'rtl';
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);

  const closeMega = () => {
    if (!megaOpen) return;
    setClosing(true);
    window.setTimeout(() => {
      setClosing(false);
      setMegaOpen(false);
    }, 220);
  };

  const iconFor = (href: string) => {
    if (href.startsWith('/about')) return <MdInfo className="w-4 h-4" />;
    if (href.startsWith('/departments')) return <MdBusiness className="w-4 h-4" />;
    if (href.startsWith('/delegations')) return <MdGroups className="w-4 h-4" />;
    if (href.startsWith('/e-services/oor')) return <MdComputer className="w-4 h-4" />;
    if (href.startsWith('/e-forms')) return <MdDescription className="w-4 h-4" />;
    if (href.startsWith('/inquiries')) return <HelpCircle className="w-4 h-4" />;
    if (href.startsWith('/complaints')) return <MdReportProblem className="w-4 h-4" />;
    if (href.startsWith('/tests')) return <MdQuiz className="w-4 h-4" />;
    if (href.startsWith('/security-clearance')) return <MdSecurity className="w-4 h-4" />;
    if (href.startsWith('/map')) return <Map className="w-4 h-4" />;
    if (href.startsWith('/archive')) return <MdArticle className="w-4 h-4" />;
    if (href.startsWith('/tenders')) return <MdGavel className="w-4 h-4" />;
    if (href.startsWith('/events')) return <MdEvent className="w-4 h-4" />;
    if (href.startsWith('/talents')) return <MdStar className="w-4 h-4" />;
    if (href.startsWith('/partners')) return <MdHandshake className="w-4 h-4" />;
    if (href.startsWith('/gulf-cup')) return <MdEmojiEvents className="w-4 h-4" />;
    if (href.startsWith('/world-bank')) return <MdPublic className="w-4 h-4" />;
    if (href.startsWith('/special-needs')) return <TbDisabled className="w-4 h-4" />;
    if (href.startsWith('/women-affairs')) return <GrRestroomWomen className="w-4 h-4" />;
    if (href.startsWith('/volunteering')) return <MdOutlineVolunteerActivism className="w-4 h-4" />;
    if (href.startsWith('/anti-doping')) return <MdMedicalServices className="w-4 h-4" />;
    if (href.startsWith('/quality-iso')) return <MdVerified className="w-4 h-4" />;
    return <MdInfo className="w-4 h-4" />;
  };

  const changeLanguage = (lang: 'ar' | 'en' | 'ku') => {
    if (lang === currentLang) {
      setLangMenuOpen(false);
      return;
    }

    // Update Redux + i18next + localStorage + <html dir/lang>
    dispatch(setLanguage(lang));
    setLangMenuOpen(false);

    // Force a full page reload so all RTK Query requests are re-fired
    // with the new language code mapped via getOdooLang.
    window.location.reload();
  };

  // Get bilingual Iraq name based on current language
  const getIraqName = () => {
    if (currentLang === 'ar') {
      return 'جمهورية العراق | کۆماری عێراق';
    } else if (currentLang === 'en') {
      return 'Republic of Iraq | کۆماری عێراق';
    } else {
      return 'کۆماری عێراق | جمهورية العراق';
    }
  };

  const navigation = [
    {
      name: t('nav.ministry.title'),
      items: [
        { name: t('nav.ministry.about'), href: '/about' },
        { name: t('nav.ministry.departments'), href: '/departments' },
        { name: t('nav.info.delegations'), href: '/delegations' },
        { name: t('partners.title') || 'شركاؤنا', href: '/partners' },
      ],
    },
    {
      name: t('nav.services.title'),
      items: [
        { name: t('nav.services.oor'), href: '/e-services/oor' },
        { name: t('nav.services.forms'), href: '/e-forms' },
        { name: t('nav.services.inquiries'), href: '/inquiries' },
        { name: t('nav.services.complaints'), href: '/complaints' },
        { name: t('nav.services.tests'), href: '/tests' },
        { name: t('nav.services.map', 'خريطة المنشآت'), href: '/map' },
        { name: t('securityClearance.title'), href: '/security-clearance' },
      ],
    },
    {
      name: t('nav.main') || 'القائمة',
      groups: [
        {
          title: t('nav.info.title'),
      items: [
        { name: t('common.news'), href: '/archive' },
        { name: t('nav.info.tenders'), href: '/tenders' },
        { name: t('nav.info.events'), href: '/events' },
        { name: t('nav.info.talents'), href: '/talents' },
      ],
    },
        {
          title: t('nav.programs.title'),
          items: [
            { name: t('nav.programs.specialNeeds'), href: '/special-needs' },
            { name: t('nav.programs.womenAffairs'), href: '/women-affairs' },
            { name: t('nav.programs.volunteering'), href: '/volunteering' },
            { name: t('nav.programs.quality'), href: '/quality-iso' },
          ],
        },
      ],
    },
    {
      name: 'المواقع الفرعية',
      items: [
        { name: 'المكتبة الإلكترونية', href: 'https://elibrary.moys.gov.iq/', external: true },
        { name: 'تكنولوجيا المعلومات', href: 'https://it.moys.gov.iq/', external: true },
        { name: 'الشركة العامة للاستثمارات الرياضية', href: 'https://gcs.moys.gov.iq/', external: true },
        { name: 'مركز الموهبة الشبابية', href: 'https://arts.moys.gov.iq/', external: true },
        { name: 'مجلة الدراسات الشبابية والتنمية', href: 'https://jssyd.moys.gov.iq/', external: true },
        { name: 'دائرة الرعاية العلمية', href: 'https://swd.moys.gov.iq/', external: true },
        { name: 'المركز الوطني لرعاية الموهبة الرياضية', href: 'https://stc.moys.gov.iq/wordpress', external: true },
        { name: 'دائرة شؤون الأقاليم والمحافظات', href: 'https://pad.moys.gov.iq', external: true },
      ],
      
    },
  ];

  useEffect(() => {
    let ticking = false;
    const update = () => {
      // Only apply scroll-based transparency on Home. Inner pages are always solid.
      if (isHome) {
        setScrolled(window.scrollY > 10);
      } else {
        setScrolled(true);
      }
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  // Keep static naming (ministry + bilingual Iraq name)

  // Close mega menu with ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMega();
    };
    if (megaOpen) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [megaOpen]);

  // Prevent background scroll when overlays are open
  useEffect(() => {
    const lock = () => {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    };
    let unlock: (() => void) | undefined;
    if (megaOpen || searchOpen) {
      unlock = lock();
    }
    return () => {
      if (unlock) unlock();
    };
  }, [megaOpen, searchOpen]);

  // Close language menu on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!langRef.current) return;
      const target = e.target as Node;
      // Don't close if clicking inside the dropdown menu
      if (langRef.current.contains(target)) {
        // Check if the click is on a language button
        const button = (target as HTMLElement).closest('button[role="menuitem"]');
        if (button) {
          // Let the button's onClick handle it
          return;
        }
      }
      setLangMenuOpen(false);
    };
    if (langMenuOpen) {
      // Use a small delay to ensure button clicks register first
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', onDocClick);
      }, 100);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', onDocClick);
      };
    }
  }, [langMenuOpen]);

  // Close mega menu on route change
  useEffect(() => {
    if (megaOpen) closeMega();
  }, [location.pathname]);

  return (
    <>
      {/* Main Header */}
      <header
        className={
          cn(
            'fixed top-0 left-0 right-0 z-[1990] transition-all',
            scrolled ? 'bg-white/90 backdrop-blur-md shadow-md border-b border-gray-200' : 'bg-transparent'
          )
        }
      >
        <div className="container-elegant">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo and Title */
            }
            <Link to="/" className="flex items-center gap-3 md:gap-4 group">
              {/* Official Ministry Logo */}
              <div className="relative">
                <img
                  src="/Icons/logo.png"
                  alt="شعار وزارة الشباب والرياضة"
                  className="w-14 h-14 md:w-16 md:h-16 object-contain group-hover:scale-105 transition-transform"
                />
              </div>

              {/* Text - Toggle between Iraq and Ministry */}
              <div className="flex flex-col items-center text-center cursor-pointer select-none justify-center" onClick={() => setShowIraq((v) => !v)} aria-label="Toggle header title">
                <h1 className={cn('text-base md:text-xl font-bold leading-tight transition-opacity duration-500', scrolled ? 'text-foreground' : 'text-white', showIraq ? 'opacity-70' : 'opacity-100')}>
                  {showIraq ? getIraqName() : t('common.ministry')}
                </h1>
                <p className={cn('text-xs md:text-sm font-bold transition-opacity duration-500', scrolled ? 'text-muted-foreground' : 'text-white/80', showIraq ? 'opacity-100' : 'opacity-70')}>
                  {showIraq ? t('common.ministry') : getIraqName()}
                </p>
            </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-2">
              {/* Language Selector in Main Header */}
              <div className="relative" ref={langRef}>
                <button
                  onClick={() => {
                    if (langMenuOpen) {
                      setLangMenuOpen(false);
                    } else {
                      setLangMenuOpen(true);
                    }
                  }}
                  className={cn(
                    'relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-300 font-semibold text-sm overflow-hidden group',
                    scrolled 
                      ? 'text-foreground hover:text-primary hover:bg-primary/5 hover:shadow-md hover:scale-105' 
                      : 'text-white hover:text-white hover:bg-white/10 hover:shadow-lg hover:scale-105',
                    langMenuOpen && 'bg-primary/10 scale-105'
                  )}
                  aria-label="Change language"
                  aria-haspopup="menu"
                  aria-expanded={langMenuOpen}
                >
                  {/* Animated background gradient */}
                  <div className={cn(
                    'absolute inset-0 bg-gradient-to-r from-primary/20 via-primary/10 to-transparent opacity-0 transition-opacity duration-300',
                    langMenuOpen && 'opacity-100'
                  )} />
                  
                  {/* Globe icon */}
                  <Globe className={cn('w-5 h-5 transition-colors duration-300 flex-shrink-0', scrolled ? 'text-foreground' : 'text-white')} />
                  
                  <span className={cn(
                    'relative uppercase font-semibold transition-all duration-300 leading-none',
                    scrolled ? 'text-foreground' : 'text-white',
                    langMenuOpen && 'font-bold'
                  )}>
                    {currentLang}
                  </span>
                  
                  <ChevronDown 
                    className={cn(
                      'w-3.5 h-3.5 transition-all duration-300 cursor-pointer flex-shrink-0',
                      scrolled ? 'text-foreground' : 'text-white',
                      langMenuOpen && 'rotate-180 scale-110'
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      setLangMenuOpen((prev) => !prev);
                    }}
                  />
                </button>
                
                {langMenuOpen && (
                  <div
                    className={cn(
                      `absolute ${isRTL ? 'left-0' : 'right-0'} mt-3 w-64 max-w-[92vw] bg-white text-foreground rounded-2xl shadow-2xl py-2 z-[3000] border border-gray-200/50 overflow-hidden lang-dropdown-in`
                    )}
                  >
                    {/* Header */}
                    <div className="px-4 py-2.5 border-b border-gray-100 bg-gradient-to-r from-primary/5 to-transparent">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('common.selectLanguage')}</p>
                    </div>
                    
                    {/* Language options */}
                    <div className="py-1">
                      {(
                        [
                          { key: 'ar', label: 'العربية', note: 'AR' },
                          { key: 'en', label: 'English', note: 'EN' },
                          { key: 'ku', label: 'کوردی', note: 'KU' },
                        ] as const
                      ).map((opt, idx) => (
                        <button
                          key={opt.key}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            changeLanguage(opt.key);
                            setLangMenuOpen(false);
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          className={cn(
                            'w-full flex items-center justify-between gap-3 px-4 py-3 text-sm transition-all duration-200 group/item relative overflow-hidden',
                            'hover:bg-gradient-to-r hover:from-primary/10 hover:to-primary/5 hover:translate-x-1',
                            'cursor-pointer active:scale-[0.98]',
                            currentLang === opt.key 
                              ? 'bg-gradient-to-r from-primary/10 to-primary/5 text-primary font-semibold' 
                              : 'text-foreground hover:text-primary'
                          )}
                          role="menuitem"
                          style={{
                            animationDelay: `${idx * 50}ms`,
                            pointerEvents: 'auto',
                          }}
                        >
                          {/* Active indicator bar */}
                          {currentLang === opt.key && (
                            <div className="absolute right-0 top-0 bottom-0 w-1 bg-primary rounded-l-full" />
                          )}
                          
                          <div className="flex items-center gap-3 flex-1">
                            <span className="font-medium group-hover/item:font-semibold transition-all duration-200">
                              {opt.label}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground font-medium px-2 py-0.5 rounded-md bg-gray-100 group-hover/item:bg-primary/10 group-hover/item:text-primary transition-colors duration-200">
                              {opt.note}
                            </span>
                            {currentLang === opt.key && (
                              <Check className="w-4 h-4 text-primary animate-in zoom-in duration-200" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
      </div>

              {/* Search Button */}
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className={cn('p-2 rounded-lg transition-colors', scrolled ? 'hover:bg-muted' : 'hover:bg-white/10')}
                aria-label="Search"
              >
                <Search className={cn('w-5 h-5', scrolled ? 'text-foreground' : 'text-white')} />
              </button>

              {/* Icon Menu at end */}
                  <button
                onClick={() => setMegaOpen((v) => !v)}
                className={cn('relative p-2 rounded-lg transition-colors', scrolled ? 'hover:bg-muted' : 'hover:bg-white/10')}
                aria-label="القائمة"
                aria-expanded={megaOpen}
              >
                <div className="relative w-6 h-6">
                  <RiMenu2Fill 
                    className={cn(
                      'absolute inset-0 w-6 h-6 transition-all duration-300',
                      scrolled ? 'text-foreground' : 'text-white',
                      megaOpen ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'
                    )} 
                  />
                  <X 
                    className={cn(
                      'absolute inset-0 w-6 h-6 transition-all duration-300',
                      scrolled ? 'text-foreground' : 'text-white',
                      megaOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'
                    )} 
                  />
                </div>
                  </button>
            </nav>

            {/* Mobile Navigation */}
            <nav 
              className="flex lg:hidden items-center gap-1.5"
              role="navigation"
              aria-label={t('nav.mobile', { defaultValue: 'القائمة المحمولة' })}
            >
              {/* Mobile Language Selector */}
              <div className="relative" ref={langRef}>
                <button
                  onClick={() => {
                    if (langMenuOpen) {
                      setLangMenuOpen(false);
                    } else {
                      setLangMenuOpen(true);
                    }
                  }}
                  className={cn(
                    'relative flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg transition-all duration-300 font-semibold text-xs overflow-hidden',
                    scrolled 
                      ? 'text-foreground hover:text-primary hover:bg-primary/5' 
                      : 'text-white hover:text-white hover:bg-white/10',
                    langMenuOpen && 'bg-primary/10'
                  )}
                  aria-label="Change language"
                  aria-haspopup="menu"
                  aria-expanded={langMenuOpen}
                >
                  {/* Globe icon */}
                  <Globe className={cn('w-4 h-4 transition-colors duration-300 flex-shrink-0', scrolled ? 'text-foreground' : 'text-white')} />
                  
                  <span className={cn(
                    'relative uppercase font-semibold transition-all duration-300 leading-none',
                    scrolled ? 'text-foreground' : 'text-white'
                  )}>
                    {currentLang}
                  </span>
                </button>
                
                {langMenuOpen && (
                  <div
                    className={cn(
                      `absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-56 max-w-[92vw] bg-white text-foreground rounded-2xl shadow-2xl py-2 z-[3000] border border-gray-200/50 overflow-hidden lang-dropdown-in`
                    )}
                  >
                    {/* Header */}
                    <div className="px-4 py-2.5 border-b border-gray-100 bg-gradient-to-r from-primary/5 to-transparent">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('common.selectLanguage')}</p>
                    </div>
                    
                    {/* Language options */}
                    <div className="py-1">
                    {(
                      [
                        { key: 'ar', label: 'العربية', note: 'AR' },
                        { key: 'en', label: 'English', note: 'EN' },
                        { key: 'ku', label: 'کوردی', note: 'KU' },
                      ] as const
                      ).map((opt, idx) => (
                      <button
                        key={opt.key}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          changeLanguage(opt.key);
                          setLangMenuOpen(false);
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        className={cn(
                            'w-full flex items-center justify-between gap-3 px-4 py-3 text-sm transition-all duration-200 group/item relative overflow-hidden',
                            'hover:bg-gradient-to-r hover:from-primary/10 hover:to-primary/5 hover:translate-x-1',
                            'cursor-pointer active:scale-[0.98]',
                            currentLang === opt.key 
                              ? 'bg-gradient-to-r from-primary/10 to-primary/5 text-primary font-semibold' 
                              : 'text-foreground hover:text-primary'
                        )}
                        role="menuitem"
                          style={{
                            animationDelay: `${idx * 50}ms`,
                            pointerEvents: 'auto',
                          }}
                        >
                          {/* Active indicator bar */}
                          {currentLang === opt.key && (
                            <div className="absolute right-0 top-0 bottom-0 w-1 bg-primary rounded-l-full" />
                          )}
                          
                          <div className="flex items-center gap-3 flex-1">
                            <span className="font-medium group-hover/item:font-semibold transition-all duration-200">
                              {opt.label}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground font-medium px-2 py-0.5 rounded-md bg-gray-100 group-hover/item:bg-primary/10 group-hover/item:text-primary transition-colors duration-200">
                          {opt.note}
                        </span>
                            {currentLang === opt.key && (
                              <Check className="w-4 h-4 text-primary animate-in zoom-in duration-200" />
                            )}
                          </div>
                      </button>
                    ))}
                    </div>
                  </div>
                )}
      </div>

              {/* Mobile Search Button */}
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className={cn('p-2 rounded-lg transition-colors', scrolled ? 'hover:bg-muted' : 'hover:bg-white/10')}
                aria-label="Search"
              >
                <Search className={cn('w-5 h-5', scrolled ? 'text-foreground' : 'text-white')} />
              </button>

              {/* Mobile Menu Button */}
                  <button
                className={cn('relative p-2 rounded-lg transition-colors', scrolled ? 'hover:bg-muted' : 'hover:bg-white/10')}
                onClick={() => setMegaOpen((v) => !v)}
                aria-label="القائمة"
                aria-expanded={megaOpen}
              >
                <div className="relative w-6 h-6">
                  <RiMenu2Fill 
                    className={cn(
                      'absolute inset-0 w-6 h-6 transition-all duration-300',
                      scrolled ? 'text-foreground' : 'text-white',
                      megaOpen ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'
                    )} 
                  />
                  <X 
                    className={cn(
                      'absolute inset-0 w-6 h-6 transition-all duration-300',
                      scrolled ? 'text-foreground' : 'text-white',
                      megaOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'
                    )} 
                  />
                </div>
              </button>
            </nav>
          </div>
        </div>

        {/* Mega Menu Overlay - Desktop and Mobile */}
        {(megaOpen || closing) && createPortal((
          <div className="fixed inset-0 z-[5000]">
            {/* Backdrop */}
            <div className={cn('absolute inset-0 bg-black/50 backdrop-blur-sm', closing ? 'mega-backdrop-out' : 'mega-backdrop')} onClick={closeMega}></div>
            {/* Menu Panel */}
            <div className={cn(
              'fixed inset-x-0',
              'top-[72px] lg:top-[96px]',
              'bottom-0 lg:bottom-auto'
            )}>
              <div className="container-elegant px-4 pb-4 lg:pb-6">
                <div className={cn('bg-white rounded-3xl shadow-elegant-lg border border-gray-200 overflow-hidden max-h-[calc(100vh-96px-1rem)] lg:max-h-[calc(100vh-96px-1.5rem)] overflow-y-auto', closing ? 'mega-panel-out' : 'mega-panel')} style={{ WebkitOverflowScrolling: 'touch' }}>
                  <div className="p-5 lg:p-7 pb-8 lg:pb-10">
                    {/* Close buttons */}
                    <div className="flex justify-end mb-4">
                      <button
                        onClick={closeMega}
                        className="hidden lg:inline-flex items-center gap-2 px-3 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200 hover:scale-110 active:scale-95"
                        aria-label="إغلاق"
                      >
                        <X className="w-4 h-4 transition-transform duration-200 hover:rotate-90" />
                      </button>
                    </div>
                    <div className="lg:hidden flex justify-end mb-6">
                  <button
                    onClick={closeMega}
                    className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200 hover:scale-110 active:scale-95"
                    aria-label="إغلاق"
                  >
                    <X className="w-5 h-5 transition-transform duration-200 hover:rotate-90" />
                  </button>
                </div>
                    {/* Menu Content - Mobile (expandable tiles) */}
                    <div className="lg:hidden space-y-3">
                      {[{ key: 'ministry', title: t('nav.ministry.title'), items: (navigation[0]?.items || []) }, { key: 'services', title: t('nav.services.title'), items: (navigation[1]?.items || []) }, { key: 'info', title: t('nav.info.title'), items: ((navigation[2] as any)?.groups?.[0]?.items || []) }, { key: 'programs', title: t('nav.programs.title'), items: ((navigation[2] as any)?.groups?.[1]?.items || []) }].map((sec) => (
                        <div key={sec.key} className="border border-gray-200 rounded-xl overflow-hidden group">
                    <button
                            className="w-full flex items-center justify-between px-4 py-3 text-foreground font-semibold hover:bg-primary/5 hover:text-primary transition-all duration-200 active:scale-[0.98]"
                            onClick={() => setMobileExpanded((s) => (s === sec.key ? null : sec.key))}
                            aria-expanded={mobileExpanded === sec.key}
                    >
                            <span className="transition-transform duration-200 group-hover:translate-x-1">{sec.title}</span>
                            <ChevronDown className={cn('w-4 h-4 transition-all duration-300', mobileExpanded === sec.key && 'rotate-180 text-primary')} />
                    </button>
                          {mobileExpanded === sec.key && (
                            <div className="pb-2">
                              {sec.items.map((sub: any) => (
                                sub.external ? (
                            <a
                                    key={sub.href}
                                    href={sub.href}
                              target="_blank"
                              rel="noopener noreferrer"
                                    onClick={() => setMegaOpen(false)}
                                    className="group/item block px-4 py-2 text-muted-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200 hover:translate-x-2 hover:font-medium active:scale-[0.98]"
                            >
                                    {sub.name}
                            </a>
                          ) : (
                            <Link
                                    key={sub.href}
                                    to={sub.href}
                                    onClick={() => setMegaOpen(false)}
                                    className="group/item block px-4 py-2 text-muted-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200 hover:translate-x-2 hover:font-medium active:scale-[0.98]"
                            >
                                    {sub.name}
                            </Link>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                    </div>

                    {/* Menu Content - Desktop grid */}
                    <div className="hidden lg:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:divide-x lg:divide-gray-100">
                  {/* About/Ministry */}
                      <div className={cn('group', isRTL ? 'lg:pl-8' : 'lg:pr-8')}>
                        <h3 className="text-xl font-bold mb-2 text-foreground group-hover:translate-x-1 transition-transform duration-200">{t('nav.ministry.title')}</h3>
                        <div className="h-1.5 w-16 bg-primary rounded-full mb-4 group-hover:w-20 transition-all duration-300"></div>
                        <div className="space-y-1">
                      {(navigation[0]?.items || []).map((sub: any) => (
                            <Link
                              key={sub.href}
                              to={sub.href}
                              onClick={() => setMegaOpen(false)}
                              className="group/item flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-primary/5 hover:text-primary text-sm font-medium text-foreground transition-all duration-200 hover:translate-x-1 hover:shadow-sm"
                            >
                              <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary hidden lg:flex items-center justify-center group-hover/item:bg-primary group-hover/item:text-white group-hover/item:scale-110 group-hover/item:rotate-3 transition-all duration-200">
                                {iconFor(sub.href)}
                              </span>
                              <span className="truncate group-hover/item:font-semibold transition-all duration-200">{sub.name}</span>
                            </Link>
                      ))}
                        </div>
                      </div>
                  {/* Services */}
                      <div className={cn('group', isRTL ? 'lg:pl-8' : 'lg:pr-8')}>
                        <h3 className="text-xl font-bold mb-2 text-foreground group-hover:translate-x-1 transition-transform duration-200">{t('nav.services.title')}</h3>
                        <div className="h-1.5 w-16 bg-primary rounded-full mb-4 group-hover:w-20 transition-all duration-300"></div>
                        <div className="space-y-1">
                      {(navigation[1]?.items || []).map((sub: any) => (
                        sub.external ? (
                              <a
                                key={sub.href}
                                href={sub.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setMegaOpen(false)}
                                className="group/item flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-primary/5 hover:text-primary text-sm font-medium text-foreground transition-all duration-200 hover:translate-x-1 hover:shadow-sm"
                              >
                                <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary hidden lg:flex items-center justify-center group-hover/item:bg-primary group-hover/item:text-white group-hover/item:scale-110 group-hover/item:rotate-3 transition-all duration-200">
                                  {iconFor(sub.href)}
                                </span>
                                <span className="truncate group-hover/item:font-semibold transition-all duration-200">{sub.name}</span>
                              </a>
                        ) : (
                              <Link
                                key={sub.href}
                                to={sub.href}
                                onClick={() => setMegaOpen(false)}
                                className="group/item flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-primary/5 hover:text-primary text-sm font-medium text-foreground transition-all duration-200 hover:translate-x-1 hover:shadow-sm"
                              >
                                <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary hidden lg:flex items-center justify-center group-hover/item:bg-primary group-hover/item:text-white group-hover/item:scale-110 group-hover/item:rotate-3 transition-all duration-200">
                                  {iconFor(sub.href)}
                                </span>
                                <span className="truncate group-hover/item:font-semibold transition-all duration-200">{sub.name}</span>
                              </Link>
                        )
                      ))}
                        </div>
                      </div>
                  {/* Info */}
                      <div className={cn('group', isRTL ? 'lg:pl-8' : 'lg:pr-8')}>
                        <h3 className="text-xl font-bold mb-2 text-foreground group-hover:translate-x-1 transition-transform duration-200">{t('nav.info.title')}</h3>
                        <div className="h-1.5 w-16 bg-primary rounded-full mb-4 group-hover:w-20 transition-all duration-300"></div>
                        <div className="space-y-1">
                      {((navigation[2] as any)?.groups?.[0]?.items || []).map((sub: any) => (
                            <Link
                              key={sub.href}
                              to={sub.href}
                              onClick={() => setMegaOpen(false)}
                              className="group/item flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-primary/5 hover:text-primary text-sm font-medium text-foreground transition-all duration-200 hover:translate-x-1 hover:shadow-sm"
                            >
                              <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary hidden lg:flex items-center justify-center group-hover/item:bg-primary group-hover/item:text-white group-hover/item:scale-110 group-hover/item:rotate-3 transition-all duration-200">
                                {iconFor(sub.href)}
                              </span>
                              <span className="truncate group-hover/item:font-semibold transition-all duration-200">{sub.name}</span>
                            </Link>
                      ))}
                        </div>
                      </div>
                  {/* Programs */}
                      <div className={cn('group', isRTL ? 'lg:pl-8' : 'lg:pr-8')}>
                        <h3 className="text-xl font-bold mb-2 text-foreground group-hover:translate-x-1 transition-transform duration-200">{t('nav.programs.title')}</h3>
                        <div className="h-1.5 w-16 bg-primary rounded-full mb-4 group-hover:w-20 transition-all duration-300"></div>
                        <div className="space-y-1">
                      {((navigation[2] as any)?.groups?.[1]?.items || []).map((sub: any) => (
                            <Link
                              key={sub.href}
                              to={sub.href}
                              onClick={() => setMegaOpen(false)}
                              className="group/item flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-primary/5 hover:text-primary text-sm font-medium text-foreground transition-all duration-200 hover:translate-x-1 hover:shadow-sm"
                            >
                              <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover/item:bg-primary group-hover/item:text-white group-hover/item:scale-110 group-hover/item:rotate-3 transition-all duration-200">
                                {iconFor(sub.href)}
                              </span>
                              <span className="truncate group-hover/item:font-semibold transition-all duration-200">{sub.name}</span>
                            </Link>
                      ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ), document.body)}

      </header>

      {/* Search Overlay */}
      {searchOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm" 
          onClick={() => setSearchOpen(false)}
        >
          <div className="container-elegant pt-16 sm:pt-20 px-4" onClick={(e) => e.stopPropagation()}>
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-elegant-lg p-4 sm:p-6">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);
                    const query = (formData.get('search') as string)?.trim();
                    if (query && query.length >= 2) {
                      setSearchOpen(false);
                      navigate(`/search?q=${encodeURIComponent(query)}`);
                    }
                  }}
                >
                  <input
                    type="text"
                    name="search"
                    placeholder={t('common.search')}
                    className="w-full px-4 sm:px-6 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-primary focus:outline-none"
                    autoFocus
                  />
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
